import {Box, Grid} from "@mui/material";
import {useState} from "react";
import {useSelector} from "react-redux";
import HelpSidebar from "./HelpSidebar";
import TutorialVideoView from "./TutorialVideoView";
import { buildTutorialSidebarItems, findTutorialMenuEntry } from "app/utils/tutorialMenuUtils";
import { TUTORIAL_VIDEO_FOLDER, tutorialVideoUrl } from "app/utils/constants/tutorialVideos";

const { SHARED, JUNE } = TUTORIAL_VIDEO_FOLDER;

const videosToRender = {
  "Admin": [
    { title: "How to manage user access ?", src: tutorialVideoUrl(SHARED, "Managing+Access.mp4") },
  ],
  // "User": [],
  "Reference Extractor": [
    { title: "How to use Reference Extractor ?", src: tutorialVideoUrl(JUNE, "Ref+Extractor.mp4") },
  ],
  "Bibliographic Data": [
    { title: "How to fetch Bibliographic Data ?", src: tutorialVideoUrl(JUNE, "Bib+Data+US+Patent.mp4") },
  ],
  "Patent File Wrapper Downloader": [
    { title: "How to use Patent File Wrapper Downloader ?", src: tutorialVideoUrl(JUNE, "Patent+File+Wrapper.mp4") },
  ],
  "AppGen": [
    { title: "How to generate US Filing package ?", src: tutorialVideoUrl(JUNE, "Appgen+Tutorial.mp4") },
  ],
  "1449 and 892 Downloader": [
    { title: "How to download 1449 and 892 documents ?", src: tutorialVideoUrl(JUNE, "1449+%26+892.mp4") },
  ],
  "OA Shell Draft": [
    { title: "How to generate OA Shell Package?", src: tutorialVideoUrl(JUNE, "OA+Shell.mp4") },
  ],
  "Oath/Dec & ADS Downloader": [
    { title: "How to download Inventors Oath & ADS documents ?", src: tutorialVideoUrl(JUNE, "Oath+%26+Dec.mp4") },
  ],
  "POA / GPOA": [
    { title: "How to generate POA/GPOA forms ?", src: tutorialVideoUrl(JUNE, "POA.mp4") },
  ],
  "SB-08 Generator": [
    { title: "How to generate SB-08 form ?", src: tutorialVideoUrl(JUNE, "SB+08.mp4") },
  ],
  "Reference Count": [
    { title: "How to use Reference Count module ?", src: tutorialVideoUrl(JUNE, "Ref+Count.mp4") },
  ],
  "PoA/Revocation of PoA/Correspondence Add Change": [
    { title: "How to generate AIA-81A (PoA/Revocation of PoA/Correspondence Add Change) form ?", src: tutorialVideoUrl(JUNE, "AIA+81+A.mp4") },
  ],
  "Attorney Withdrawal & Address Change Request": [
    { title: "How to generate AIA-83 (Attorney Withdrawal & Address Change Request) form?", src: tutorialVideoUrl(JUNE, "AIA+83.mp4") },
  ],
  "Claims Formatter": [
    { title: "How to use Claims Formatter ?", src: tutorialVideoUrl(JUNE, "Claims_Formatter.mp4") },
  ],
  "Reference Downloader": [
    { title: "How to use Reference Downloader ?", src: tutorialVideoUrl(JUNE, "Reference_Downloader.mp4") },
  ],
  "Correct or Update Applicant Name(1.46)": [
    { title: "How to use 'Correct or Update Applicant Name(1.46)' ?", src: tutorialVideoUrl(JUNE, "intent_146.mp4") },
  ],
  "37 CFR or 3.73(c)": [
    { title: "How to use 37 CFR ?", src: tutorialVideoUrl(JUNE, "37_CFR.mp4") },
  ],
  "RCE": [
    { title:    "How to generate RCE Transmittal form (EFS) ?", src: tutorialVideoUrl(JUNE, "SB_30_EFS.mp4") },
  ],
  "US TM Image Download": [
    { title: "How to use US TM Image Download ?", src: tutorialVideoUrl(JUNE, "US_TM_Image_Download.mp4") },
  ],
  "Remove Embedded Fonts": [
    { title: "How to use Remove Embedded Fonts ?", src: tutorialVideoUrl(JUNE, "Remove Embedded Fonts.mp4") },
  ],
};

const TutorialSection = () => {
  const [tab, setTab] = useState("AppGen");

  const { intentList } = useSelector((state) => ({ ...state }));

  // Basic general items
  let generalItems = [
    { label: "Admin", subMenus: [], subKey: "" },
  ];

  if (localStorage.getItem("TENANT_ROLE") === "user") {
    generalItems = generalItems.slice(1);
  }

  const sidebarItems = [...generalItems, ...buildTutorialSidebarItems(intentList)];

  // Fused menus share a single video (keyed by the menu label) across their sub-intents.
  const fusedSubIntents = findTutorialMenuEntry(sidebarItems, tab)?.subIntents;
  const videos = videosToRender[tab];

  return (
    <Grid
      container
      spacing={2}
      sx={{
        height: "100%",
        minHeight: 0,
        bgcolor: "#f5f8fc",
        py: 2,
        overflow: "hidden",
      }}
    >
      {/* Sidebar column */}
      <Grid
        item
        xs={12}
        md={3}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden", 
          }}
        >
          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: "10px",
              },
              scrollbarWidth: "thin",
              scrollbarGutter: "stable",
            }}
          >
            <HelpSidebar
              sidebarItems={sidebarItems}
              tab={tab}
              setTab={setTab}
            />
          </Box>
        </Box>
      </Grid>

      {/* Video content column */}
      <Grid
        item
        xs={12}
        md={9}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              // The tutorial view fills this panel, so it only scrolls when the
              // window is too short for the header plus the minimum player height.
              overflowY: "auto",
              flex: 1,
              p: 4,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: "10px",
              },
              scrollbarWidth: "thin",
            }}
          >
            <TutorialVideoView key={tab} title={tab} subIntents={fusedSubIntents} videos={videos} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TutorialSection;